import { useEffect, useRef, type ReactNode, useCallback } from "react";
import { createPortal } from "react-dom";
import { IconX } from "@tabler/icons-react";
import { hasDismissOwner } from "@/lib/a11y/dismissOwner";
import { TooltipLayer } from "@/components/PInfo/PInfo";
import { hintZAbove } from "@/components/PInfo/tooltipLayer";

type Props = {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  size?: "sm" | "md" | "lg" | "xl" | "2xl";
  closeOnBackdrop?: boolean;
  /**
   * Stacking class. Defaults above plain content but BELOW high-z hosts; pass a
   * higher class (e.g. "z-[10070]") when this Modal opens from inside another
   * overlay (a P-modal / elevated studio host) so it isn't occluded.
   */
  zClassName?: string;
  /** Drop the divider line under the header (and its padding becomes header-only). */
  hideHeaderBorder?: boolean;
  /**
   * Receives the Modal's ROOT node, so a caller's focus trap can span the
   * header's Close button too — a trap on the caller's own inner panel left
   * that button unreachable by Tab (audit 2026-09-28). Optional, additive.
   */
  panelRef?: React.MutableRefObject<HTMLDivElement | null>;
};

const SIZE = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
  // A 900-px mail's preview frame (940 px) does not fit max-w-4xl (896 px).
  "2xl": "max-w-6xl",
};

export function Modal({
  open,
  onClose,
  title,
  children,
  size = "md",
  closeOnBackdrop = true,
  zClassName = "z-[900]",
  hideHeaderBorder = false,
  panelRef,
}: Props) {
  // Set while the press goes DOWN on the backdrop, read on the click that
  // follows — see `hasDismissOwner`.
  const pressDismissedAChild = useRef(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  // Stable, so React does not detach/re-attach the ref on every render.
  const setRoot = useCallback(
    (el: HTMLDivElement | null) => {
      rootRef.current = el;
      if (panelRef) panelRef.current = el;
    },
    [panelRef],
  );

  // Escape stops here.
  //
  // This used to listen on `document` in the BUBBLE phase and neither
  // `preventDefault` nor `stopPropagation` — so a host that also listens on
  // `window` (every WizardShell does, bailing only on `defaultPrevented`) ran
  // its handler on the same keypress. Opening a template preview inside the
  // website wizard and pressing Escape closed the preview AND exited the
  // wizard AND wiped its saved draft, with no confirmation. Four wizards
  // carried that through this one component.
  //
  // Capture on `window` is the only phase that wins: the host mounts first, so
  // a same-node bubble listener can never get ahead of it. Standing down for
  // inner owners is therefore mandatory, not defensive — `ConditionsDrawer` is
  // a Modal, and without the carve-out Escape on an open dropdown inside it
  // would tear the whole drawer down. Same contract as HubDialogShell.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      const target = e.target;
      if (target instanceof Element && target.closest('[data-escape-owner="true"]')) return;
      // Focus is not always INSIDE the owner: a press on a picker list's
      // header can leave it on <body> while the list is still open. The list
      // is still ours to stand down for — ask the panel, as PSeatCellEditor
      // does, not only the key's target.
      if (rootRef.current?.querySelector('[data-escape-owner="true"]')) return;
      e.preventDefault();
      e.stopPropagation();
      onClose();
    };
    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    // sm:pr-[calc(1rem+var(--ai-rail-push))]: keep the p-4 gutter but center
    // the panel in the app area LEFT of the docked AI drawer rail (340px,
    // z-10000) instead of under it. 0px while the drawer is closed.
    <div
      // Lets an outer shell that portals its own overlay stand down while this
      // one is on top — same marker PConfirmDialog uses, and HubDialogShell
      // already checks for it.
      data-nested-dialog="true"
      ref={setRoot}
      className={`fixed inset-0 ${zClassName} flex items-center justify-center bg-black/60 p-4 sm:pr-[calc(1rem+var(--ai-rail-push))] transition-[padding] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none`}
      // One click, one dismissal: a press on the backdrop while a child
      // overlay (picker list, calendar, inline editor) is open belongs to that
      // child. Without this the same click would also close the modal — and
      // this host's `onClose` is a DISCARD for some callers (ConditionsDrawer).
      // Asked on mousedown because the child is already gone by click time.
      onMouseDownCapture={(e) => {
        pressDismissedAChild.current =
          e.target === e.currentTarget && hasDismissOwner(e.currentTarget as Element);
      }}
      onClick={() => {
        if (pressDismissedAChild.current) {
          pressDismissedAChild.current = false;
          return;
        }
        if (closeOnBackdrop) onClose();
      }}
    >
      <div
        // max-h + column flex: long content scrolls INSIDE the panel instead
        // of pushing it past short/landscape viewports (header stays visible).
        className={`w-full ${SIZE[size]} max-h-[calc(100dvh-2rem)] flex flex-col rounded-xl border outline-quaternary surface-primary shadow-2xl`}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`flex items-center justify-between px-4 py-3 ${
            hideHeaderBorder ? "" : "border-b outline-quaternary"
          }`}
        >
          <h2 className="body-1-bold font-primary">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 font-tertiary hover:surface-tertiary"
            aria-label="Close"
          >
            <IconX size={18} stroke={1.75} />
          </button>
        </div>
        <div className="p-4 font-primary min-h-0 overflow-y-auto">
          {/* A Modal raised above the hint rung (z-10070 …) paints its own
              hints above itself, not under (audit hop3 r2 M1). */}
          <TooltipLayer z={hintZAbove(zClassName)}>{children}</TooltipLayer>
        </div>
      </div>
    </div>,
    document.body,
  );
}
